import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { bannersAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Image as ImageIcon, Plus, Edit, Trash2, Save, X, Eye, EyeOff } from 'lucide-react';

const AdminBanners = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '', subtitle: '', description: '', imageUrl: '',
    linkUrl: '', buttonText: '', position: 'hero', active: true, displayOrder: 0
  });

  useEffect(() => { loadBanners(); }, []);

  const loadBanners = async () => {
    try {
      const data = await bannersAPI.list();
      setBanners(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ title: '', subtitle: '', description: '', imageUrl: '', linkUrl: '', buttonText: '', position: 'hero', active: true, displayOrder: 0 });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (banner) => {
    setForm({
      title: banner.title || '', subtitle: banner.subtitle || '', description: banner.description || '',
      imageUrl: banner.imageUrl || '', linkUrl: banner.linkUrl || '', buttonText: banner.buttonText || '',
      position: banner.position || 'hero', active: banner.active !== false, displayOrder: banner.displayOrder || 0
    });
    setEditing(banner.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.imageUrl) { alert('Preencha Título e URL da Imagem'); return; }
    setSaving(true);
    try {
      if (editing) {
        await bannersAPI.update(editing, form);
      } else {
        await bannersAPI.create(form);
      }
      resetForm();
      loadBanners();
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    } finally { setSaving(false); }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Excluir banner "${title}"?`)) return;
    try {
      await bannersAPI.delete(id);
      setBanners(banners.filter(b => b.id !== id));
    } catch (error) {
      alert('Erro: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      await bannersAPI.update(banner.id, { active: !banner.active });
      setBanners(banners.map(b => b.id === banner.id ? { ...b, active: !b.active } : b));
    } catch (e) { alert('Erro ao atualizar'); }
  };

  const positionLabels = { hero: 'Hero Principal', secondary: 'Secundário', promotional: 'Promocional', sidebar: 'Lateral' };

  return (
    <AdminLayout>
      <div>
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 data-testid="banners-page-title" className="text-2xl font-bold text-gray-800">Banners</h1>
            <p className="text-gray-600">{banners.length} banners</p>
          </div>
          <Button data-testid="add-banner-btn" onClick={() => { resetForm(); setShowForm(true); }} className="bg-[#055509] hover:bg-[#0a7a0f] text-white">
            <Plus size={18} className="mr-2" /> Novo Banner
          </Button>
        </div>

        {showForm && (
          <Card className="mb-6 border-[#055509]">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editing ? 'Editar Banner' : 'Novo Banner'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}><X size={18} /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
                    <Input data-testid="banner-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Subtítulo</label>
                    <Input value={form.subtitle} onChange={e => setForm({ ...form, subtitle: e.target.value })} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL da Imagem *</label>
                  <Input data-testid="banner-image-input" value={form.imageUrl} onChange={e => setForm({ ...form, imageUrl: e.target.value })} required />
                </div>
                {form.imageUrl && <img src={form.imageUrl} alt="Preview" className="w-full max-h-48 object-cover rounded-lg" />}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Posição</label>
                    <select data-testid="banner-position-select" value={form.position} onChange={e => setForm({ ...form, position: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm">
                      <option value="hero">Hero Principal</option>
                      <option value="secondary">Secundário</option>
                      <option value="promotional">Promocional</option>
                      <option value="sidebar">Lateral</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
                    <Input value={form.linkUrl} onChange={e => setForm({ ...form, linkUrl: e.target.value })} placeholder="/produtos" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Texto do Botão</label>
                    <Input value={form.buttonText} onChange={e => setForm({ ...form, buttonText: e.target.value })} placeholder="Saiba mais" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
                  <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px]" />
                </div>
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.active} onChange={e => setForm({ ...form, active: e.target.checked })} className="rounded" />
                    <span className="text-sm">Banner Ativo</span>
                  </label>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mr-2">Ordem:</label>
                    <Input type="number" value={form.displayOrder} onChange={e => setForm({ ...form, displayOrder: parseInt(e.target.value) || 0 })} className="w-20 inline-block" />
                  </div>
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancelar</Button>
                  <Button data-testid="save-banner-btn" type="submit" className="bg-[#055509] hover:bg-[#0a7a0f] text-white" disabled={saving}>
                    {saving ? 'Salvando...' : <><Save size={16} className="mr-2" />{editing ? 'Atualizar' : 'Criar'}</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#055509]" /></div>
            ) : banners.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ImageIcon size={48} className="mx-auto mb-4 opacity-50" />
                <p>Nenhum banner cadastrado</p>
              </div>
            ) : (
              <div className="divide-y">
                {banners.map(banner => (
                  <div key={banner.id} className="p-4 flex items-center gap-4 hover:bg-gray-50" data-testid={`banner-row-${banner.id}`}>
                    <img src={banner.imageUrl} alt={banner.title} className="w-24 h-16 object-cover rounded" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-800">{banner.title}</div>
                      <div className="text-xs text-gray-500">{positionLabels[banner.position] || banner.position} • Ordem: {banner.displayOrder}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button data-testid={`toggle-banner-${banner.id}`} onClick={() => handleToggleActive(banner)} className={`p-1 rounded ${banner.active ? 'text-green-500' : 'text-gray-300'}`}>
                        {banner.active ? <Eye size={16} /> : <EyeOff size={16} />}
                      </button>
                      <Button data-testid={`edit-banner-${banner.id}`} variant="ghost" size="sm" onClick={() => handleEdit(banner)}><Edit size={16} /></Button>
                      <Button data-testid={`delete-banner-${banner.id}`} variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(banner.id, banner.title)}><Trash2 size={16} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminBanners;
